import React, { useEffect, useState } from 'react';
import { Box, CloseButton, Flex, IconButton, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';

const ACTIVE_ANNOUNCEMENTS_QUERY = `
  query ActiveAppAnnouncements($route: String!) {
    activeAppAnnouncements(route: $route) {
      id
      content
      translations {
        languageCode
        content
      }
    }
  }
`;

async function fetchActiveAnnouncements(apiUrl, authToken, route) {
  const response = await fetch(`${apiUrl}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `AuthToken token=${authToken}`,
    },
    body: JSON.stringify({
      query: ACTIVE_ANNOUNCEMENTS_QUERY,
      variables: { route },
    }),
  });

  const json = await response.json();
  return json.data?.activeAppAnnouncements ?? [];
}

const ANGULAR_ROUTE_PREFIX = '/pvt/#';

export default function AppAnnouncementBanner({ apiUrl, authToken, route, languageCode }) {
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);

  const fullRoute = `${ANGULAR_ROUTE_PREFIX}${route}`;

  const { data: activeAnnouncements } = useQuery({
    queryKey: ['activeAppAnnouncements', fullRoute],
    queryFn: () => fetchActiveAnnouncements(apiUrl, authToken, fullRoute),
    enabled: !!apiUrl && !!authToken && !!route,
  });

  const announcements = activeAnnouncements ?? [];
  const visibleAnnouncements = announcements.filter((a) => !dismissedIds.has(a.id));

  const resolveContent = (announcement) => {
    if (languageCode) {
      const translation = announcement.translations?.find(
        (tr) => tr.languageCode === languageCode
      );
      if (translation?.content) return translation.content;
    }
    return announcement.content;
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [route]);

  const dismiss = (id) => {
    setDismissedIds((prev) => new Set([...prev, id]));
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  if (visibleAnnouncements.length === 0) return null;

  const current = visibleAnnouncements[currentIndex];
  const hasMultiple = visibleAnnouncements.length > 1;

  return (
    <Box
      position="fixed"
      top="20px"
      left="50%"
      transform="translateX(-50%)"
      width={{ base: '95%', lg: '50%' }}
      backgroundColor="#102d47"
      paddingY="20px"
      paddingX="40px"
      borderRadius="8px"
      boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
      zIndex={1000}
    >
      <Text
        color="white"
        textAlign="center"
        fontWeight={500}
        fontSize={{ base: '12px', md: '14px' }}
      >
        {resolveContent(current)}
      </Text>

      {hasMultiple && (
        <Flex justifyContent="center" gap={2} marginTop={2}>
          {visibleAnnouncements.map((announcement, idx) => (
            <Box
              key={announcement.id}
              width="6px"
              height="6px"
              borderRadius="full"
              backgroundColor={idx === currentIndex ? 'white' : 'whiteAlpha.400'}
              cursor="pointer"
              onClick={() => setCurrentIndex(idx)}
            />
          ))}
        </Flex>
      )}

      {hasMultiple && currentIndex > 0 && (
        <IconButton
          aria-label="Previous announcement"
          icon={<Text color="white" fontSize="18px">&#8249;</Text>}
          variant="ghost"
          position="absolute"
          left="8px"
          top="50%"
          transform="translateY(-50%)"
          onClick={() => setCurrentIndex((prev) => prev - 1)}
          _hover={{ backgroundColor: 'whiteAlpha.200' }}
        />
      )}

      {hasMultiple && currentIndex < visibleAnnouncements.length - 1 && (
        <IconButton
          aria-label="Next announcement"
          icon={<Text color="white" fontSize="18px">&#8250;</Text>}
          variant="ghost"
          position="absolute"
          right="30px"
          top="50%"
          transform="translateY(-50%)"
          onClick={() => setCurrentIndex((prev) => prev + 1)}
          _hover={{ backgroundColor: 'whiteAlpha.200' }}
        />
      )}

      <CloseButton
        color="white"
        onClick={() => dismiss(current.id)}
        position="absolute"
        top="5px"
        right="5px"
      />
    </Box>
  );
}
